/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Settings search (v2). Matches a query against each card's title and
 * description, and against the areas inside it, so "currency" finds Company
 * Settings and "phone menus" finds Telephony.
 *
 * Areas are the tab, section and field names each settings page shows today.
 * `to` is set only where the page can open that area by URL; the others open
 * the card itself. Keep this list in step with the pages when they change.
 */

export const SETTINGS_AREAS = {
  "Company Settings": [
    "Company Details", "Geography", "Custom Fields", "Templates", "Tax Details", "Upload Logo",
    "Company Information", "Registered Address", "Banking Information", "Currency", "GST State Code", "Tax Type",
  ],
  "User Management": [
    "Add New User", "License Details", "Usage Statistics", "Expiration Details", "Seats Used",
    "Upgrade License", "Role", "Department", "Manager", "Designation",
  ],
  "Roles & Permissions": ["Create role", "Permissions", "People"],
  Teams: ["Create team", "Sub-teams"],
  "Automation Builder": [
    "Create Automation", "Import JSON", "Browse Templates", "Prebuilt Automation Templates", "Describe your automation",
  ],
  "Custom Objects": [{ label: "New custom object", to: "/settings/custom-objects/new" }],
  "Page Layouts": ["Page Layout Settings", "Show or hide sections", "Drag to reorder"],
  "Sidebar Navigation": ["Add section", "Add module", "Reset to default", "Add custom section", "Add custom module"],
  Integrations: [
    { label: "Webhooks", to: "/settings/integrations?section=webhooks" },
    { label: "Add Webhook", to: "/settings/integrations?section=webhooks" },
    { label: "API Integrations", to: "/settings/integrations?section=api" },
    { label: "Google Maps Integration", to: "/settings/integrations?section=api" },
    { label: "AI Integration", to: "/settings/integrations?section=api" },
    { label: "GST Integration", to: "/settings/integrations?section=api" },
    { label: "Social", to: "/settings/integrations?section=social" },
    { label: "WhatsApp Business", to: "/settings/integrations?section=social" },
  ],
  "Real Estate": [
    "Personas", "Your business", "Developer", "Property agent", "Agency", "Property manager",
    "Mortgage broker", "Region", "Country", "Currency", "Tax",
  ],
  Travel: [
    "Enable Travel Module", "Trip", "Departure Dates", "Itinerary", "Payment Templates", "Payment Terms",
    "Coupons", "Loyalty Rules", "Loyalty Tiers", "Referral Program Settings", "Merchants", "Travel Invoices", "CMS Publisher",
  ],
  Website: [
    { label: "Design", to: "/website?tab=design" },
    { label: "Domain", to: "/website?tab=domain" },
    { label: "Custom domain", to: "/website?tab=domain" },
    { label: "Content", to: "/website?tab=content" },
    { label: "Packages", to: "/website?tab=packages" },
    "Build your home page",
  ],
  Payments: [{ label: "Stripe", to: "/settings/payments/stripe" }, "Razorpay"],
  Telephony: [
    { label: "Twilio connection", to: "/settings/telephony/twilio" },
    { label: "Phone numbers", to: "/settings/telephony/numbers" },
    { label: "Phone menus", to: "/settings/telephony/ivr" },
    { label: "Queues & routing", to: "/settings/telephony/routing" },
    { label: "Recording & transcription", to: "/settings/telephony/recording" },
  ],
  "SEEP AI": [
    "Knowledge", "Conversations", "Analytics & minutes", "Voice", "Personality", "Languages",
    "Handing over to your team", "Greeting", "Widget colour", "Minutes left",
  ],
  "AI Assistant": ["Seats", "Usage", "Credits left", "Monthly allowance", "Purchased credits"],
  "Connected apps": ["Disconnect"],
  "Developer & API": ["Clients", "New client", "Activity", "API reference", "Rotate client secret"],
  Queues: [
    "New queue", "Voice settings", "Add channel", "Round robin", "Least loaded", "Longest idle",
    { label: "Capacity", to: "/settings/queues/capacity" },
  ],
  Skills: ["New skill"],
  "Support Access": ["Support Access Control"],
  "Web Forms": [
    "Web-to-Lead", "Web-to-Case", "Website Domain", "Form Title", "Success Message",
    "Custom Styling", "Primary Color", "Form Fields", "Generated Form Code",
  ],
  "Email to Case": [
    "Inbound domain & DNS", "Brevo keys", "Webhook", "Forwarding", "Reopen closed cases on customer reply",
    "Rotate webhook token", "Gmail", "Outlook",
  ],
};

const normalise = (text) => String(text || "").toLowerCase();

/** Every word of the query appears somewhere in the text, in any order. */
function matchesAll(words, text) {
  const hay = normalise(text);
  return words.every((w) => hay.includes(w));
}

/**
 * Filters `cards` ({ title, description }) by `query`. Returns the cards to
 * show, in their original order, each with `areas`: the inner areas that
 * matched ({ label, to }), empty when only the title or description did.
 * An empty query returns every card with no areas.
 */
export function searchSettings(cards, query, areas = SETTINGS_AREAS) {
  const words = normalise(query).split(/\s+/).filter(Boolean);
  if (!words.length) return cards.map((card) => ({ card, areas: [] }));
  const out = [];
  cards.forEach((card) => {
    const own = matchesAll(words, `${card.title} ${card.description}`);
    const inner = (areas[card.title] || [])
      .map((a) => (typeof a === "string" ? { label: a, to: null } : { to: null, ...a }))
      .filter((a) => matchesAll(words, a.label));
    if (own || inner.length) out.push({ card, areas: inner });
  });
  return out;
}
